import { ChangeDetectionStrategy, Component, computed, inject, signal } from "@angular/core";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { ContactService } from "../../services/contact.service";
import { SeSecurityStore } from "./se.security.store";
import { s25Resource } from "../../util/s25.resource";
import { S25LoadingInlineModule } from "../s25-loading/s25.loading.inline.module";
import { S25ContactInfoModule } from "../s25-contact-info/s25.contact.module";
import { Item } from "../../pojo/Item";
import { UserprefService } from "../../services/userpref.service";
import { S25FilestoreImageComponent } from "../../standalone/s25.filestore.image.component";
import { StudentEngagementUtil } from "./student.engagement.util";
import { CustomAttributes } from "../../pojo/CustomAttributes";
import { CustomAttributeValueUpdateService } from "../../services/custom.attribute.value.update.service";
import { SeProfileUiStore } from "./contact/se.profile.ui.store";
import { S25Util } from "../../util/s25-util";

@TypeManagerDecorator("s25-ng-user-profile")
@Component({
    selector: "s25-ng-user-profile",
    template: `
        <div class="user-settings-page">
            <div class="page-header">
                <h1>User Settings</h1>
            </div>

            @if (contactImageUrlResource.resource.isLoading()) {
                <div class="loading-container">
                    <s25-ng-loading-inline-static></s25-ng-loading-inline-static>
                </div>
            } @else {
                <div class="user-settings-body" [class.user-settings-body--split]="hasStudentEngagementAccess()">
                    @if (hasStudentEngagementAccess()) {
                        <div class="form-group">
                            <h2 class="section-heading">Profile Image</h2>
                            <div class="profile-image-wrapper">
                                <s25-ng-filestore-image
                                    [value]="contactImageUrlResource.value() ?? ''"
                                    [readOnly]="!canEdit()"
                                    [circle]="true"
                                    [objectId]="contactId()"
                                    [objectType]="itemIds.Contact"
                                    [customAttributeId]="profileImageAttrId"
                                    [autoSaveOnClear]="true"
                                    (valueChange)="imageUpdated($event)"
                                ></s25-ng-filestore-image>
                            </div>
                        </div>
                    }
                    <div class="form-group">
                        <h2 class="section-heading">Contact information</h2>
                        <s25-ng-contact-info
                            [itemId]="contactId()"
                            [canEdit]="canEdit()"
                            [fields]="{ hasEmailOptions: false, showEmail: true, showAddress: false }"
                            [data]="{ userSettings: true, enableAutocomplete: true }"
                        ></s25-ng-contact-info>
                    </div>
                </div>
            }
        </div>
    `,
    styles: `
        /* Align with my-workflow-page (.my-workflow-page) */
        .user-settings-page {
            padding: 20px;
            max-width: 1200px;
            margin: 0 auto;
        }

        .page-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 24px;
            padding-bottom: 16px;
            border-bottom: 2px solid var(--color--border);
        }

        .page-header h1 {
            margin: 0;
            color: var(--color--text-strong);
        }

        .loading-container {
            padding: 1rem 0;
        }

        /* Align with service-hours-form: .form-group, .form-label */
        .form-group {
            margin-bottom: 1rem;
        }

        .section-heading {
            margin: 0 0 0.5rem;
            font-size: 0.875rem;
            font-weight: 500;
            color: var(--color--text-strong);
        }

        .user-settings-body {
            display: flex;
            flex-direction: column;
            gap: 1rem;
        }

        /* Same idea as service-hours-form .form-row (grid), stacked on narrow viewports */
        @media (min-width: 768px) {
            .user-settings-body--split {
                display: grid;
                grid-template-columns: 1fr 1fr;
                gap: 1.5rem;
                align-items: start;
            }

            .user-settings-body--split > .form-group {
                margin-bottom: 0;
            }
        }

        .profile-image-wrapper {
            display: flex;
            align-items: flex-start;
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [S25LoadingInlineModule, S25ContactInfoModule, S25FilestoreImageComponent],
    host: {
        class: "user-profile-container s25-ng",
    },
})
export class S25UserProfileComponent {
    private securityStore = inject(SeSecurityStore);
    private seProfileUiStore = inject(SeProfileUiStore);
    protected itemIds = Item.Ids;

    protected profileImageAttrId = StudentEngagementUtil.LogoAttrIds[Item.Ids.Contact]!;

    contactId = signal<number | undefined>(undefined);

    contactImageUrlResource = s25Resource({
        loader: async () => {
            const id = await UserprefService.getContactId();
            this.contactId.set(id);

            const url = await ContactService.getContactImagePath(id);
            return url ?? "";
        },
    });

    hasStudentEngagementAccess = computed(() => {
        if (!this.securityStore.isInitialized()) {
            return false;
        }
        return this.securityStore.hasReadFls();
    });

    canEdit = computed(() => {
        return this.securityStore.flsStore.hasEdit("CU_CONTACT");
    });

    async imageUpdated(event: string) {
        if (!this.canEdit()) return;
        const contactId = this.contactId();
        if (!contactId) return;
        const prevImage = this.seProfileUiStore.profileImageValue();
        const isClear = !event?.trim();
        this.seProfileUiStore.setContactProfileImage(contactId, isClear ? undefined : event);

        //Update the custom attribute with the file reference
        const [_, error] = await S25Util.Maybe(
            CustomAttributeValueUpdateService.saveAttributeValue({
                itemTypeId: this.itemIds.Contact,
                itemId: contactId,
                attributeId: this.profileImageAttrId,
                attributeType: CustomAttributes.type.URL,
                status: isClear ? "del" : "mod",
                value: isClear ? undefined : event,
            }),
        );
        if (error) {
            this.seProfileUiStore.setContactProfileImage(contactId, prevImage);
            return console.error(error);
        }
        this.contactImageUrlResource.resource.reload();
    }
}
